import { allocateHandicapStrokes } from "./round-handicap.js";
import {
  isRoundV3,
  type CourseHoleSnapshot,
  type HoleEntryV3,
  type RoundSnapshot,
} from "./round-types.js";

export interface PlayerRoundSummary {
  actualStrokes: number | null;
  vsCoursePar: number | null;
  vsOwnPar: number | null;
  netStrokes: number | null;
  grossStableford: number | null;
  netStableford: number | null;
  playingHandicap: number | null;
  scoredHoles: number;
  pickupHoles: number;
  completedHoles: number;
  pendingHoles: number;
  totalHoles: number;
  netReady: boolean;
}

export function selectOrderedHoles(
  snapshot: RoundSnapshot,
): CourseHoleSnapshot[] {
  if (!isRoundV3(snapshot)) return snapshot.course.holes;
  return snapshot.orderedHoleNumbers.map((number) => {
    const hole = snapshot.course.holes.find((item) => item.number === number);
    if (!hole) throw new Error("The round is missing an agreed physical hole.");
    return hole;
  });
}

export function selectHoleEntry(
  snapshot: RoundSnapshot,
  targetId: string,
  holeNumber: number,
): HoleEntryV3 {
  if (isRoundV3(snapshot))
    return snapshot.holeEntries[targetId]?.[holeNumber] ?? emptyHoleEntry();
  const strokes = snapshot.scores[targetId]?.[holeNumber] ?? null;
  return {
    ...emptyHoleEntry(),
    ...snapshot.legacyHoleStatistics?.[targetId]?.[holeNumber],
    status: strokes === null ? "unset" : "scored",
    strokes,
  };
}

export function selectPlayerHole(
  snapshot: RoundSnapshot,
  participantId: string,
  holeNumber: number,
) {
  const participant = snapshot.participants.find(
    (item) => item.id === participantId,
  );
  if (!participant) throw new Error("The participant is not in this round.");
  const selected = selectOrderedHoles(snapshot);
  const numbers = selected.map((hole) => hole.number);
  const index = numbers.indexOf(holeNumber);
  if (index < 0) throw new Error("The physical hole is not in this round.");
  const teeHoles = isRoundV3(snapshot)
    ? participant.teeSnapshot?.holes
    : snapshot.course.holes;
  const holes = numbers.map((number) =>
    teeHoles?.find((hole) => hole.number === number),
  );
  const hole = holes[index];
  if (!hole || holes.some((item) => !item))
    throw new Error("The player's tee is missing a selected hole.");
  const playingHandicap = selectPlayingHandicap(snapshot, participantId);
  const ownReceivedStrokes =
    playingHandicap === null
      ? null
      : allocateHandicapStrokes(holes as CourseHoleSnapshot[], playingHandicap)[
          index
        ] || 0;
  let contestReceivedStrokes: number | null = 0;
  const net = isRoundV3(snapshot)
    ? snapshot.mainContest.scoringBasis === "net"
    : snapshot.rules.handicapMode !== "off";
  if (net) {
    const relative = isRoundV3(snapshot)
      ? snapshot.mainContest.format === "match-play"
      : snapshot.rules.handicapMode === "relative";
    const ids = isRoundV3(snapshot)
      ? snapshot.mainContest.sides.flatMap((side) => side.participantIds)
      : snapshot.participants.map((item) => item.id);
    const handicaps = ids.map((id) => selectPlayingHandicap(snapshot, id));
    const minimum =
      relative && handicaps.every((value) => value !== null)
        ? Math.min(...(handicaps as number[]))
        : 0;
    contestReceivedStrokes =
      playingHandicap === null ||
      (relative && handicaps.some((value) => value === null))
        ? null
        : allocateHandicapStrokes(
            holes as CourseHoleSnapshot[],
            playingHandicap - minimum,
          )[index] || 0;
  }
  return {
    hole,
    entry: selectHoleEntry(snapshot, participantId, holeNumber),
    ownReceivedStrokes,
    contestReceivedStrokes,
  };
}

export function selectPlayingHandicap(
  snapshot: RoundSnapshot,
  participantId: string,
): number | null {
  if (snapshot.importedTotals)
    return (
      snapshot.importedTotals.players[participantId]?.playingHandicap ?? null
    );
  const participant = snapshot.participants.find(
    (item) => item.id === participantId,
  );
  if (!participant) return null;
  if (isRoundV3(snapshot))
    return participant.handicapSnapshot?.playingHandicap ?? null;
  // Legacy gross rounds stored a synthetic zero. It is not evidence of agreed PH 0.
  return snapshot.rules.handicapMode === "off"
    ? null
    : participant.playingHandicap;
}

export function selectPlayerSummary(
  snapshot: RoundSnapshot,
  participantId: string,
  holeNumbers?: number[],
): PlayerRoundSummary {
  const ordered = selectOrderedHoles(snapshot);
  const selected = holeNumbers
    ? ordered.filter((hole) => holeNumbers.includes(hole.number))
    : ordered;
  const playingHandicap = selectPlayingHandicap(snapshot, participantId);
  const netReady = playingHandicap !== null;
  let actual = 0,
    par = 0,
    received = 0,
    grossPoints = 0,
    netPoints = 0,
    scored = 0,
    pickups = 0;
  for (const selectedHole of selected) {
    const { hole, entry, ownReceivedStrokes } = selectPlayerHole(
      snapshot,
      participantId,
      selectedHole.number,
    );
    if (entry.status === "pickup") {
      pickups += 1;
      continue;
    }
    if (entry.status !== "scored" || entry.strokes === null) continue;
    scored += 1;
    actual += entry.strokes;
    par += hole.par;
    received += ownReceivedStrokes ?? 0;
    grossPoints += Math.max(0, 2 + hole.par - entry.strokes);
    netPoints += Math.max(
      0,
      2 + hole.par + (ownReceivedStrokes ?? 0) - entry.strokes,
    );
  }
  return {
    actualStrokes: scored ? actual : null,
    vsCoursePar: scored ? actual - par : null,
    vsOwnPar: scored && netReady ? actual - par - received : null,
    netStrokes: scored && netReady ? actual - received : null,
    grossStableford: scored + pickups ? grossPoints : null,
    netStableford: scored + pickups && netReady ? netPoints : null,
    playingHandicap,
    scoredHoles: scored,
    pickupHoles: pickups,
    completedHoles: scored + pickups,
    pendingHoles: selected.length - scored - pickups,
    totalHoles: selected.length,
    netReady,
  };
}

/** First unfinished physical hole for the entire flight, including gaps. */
export function selectNextHole(snapshot: RoundSnapshot): number | null {
  const targets = Object.keys(snapshot.scores);
  return (
    selectOrderedHoles(snapshot).find((hole) =>
      targets.some(
        (id) => selectHoleEntry(snapshot, id, hole.number).status === "unset",
      ),
    )?.number ?? null
  );
}

export function emptyHoleEntry(): HoleEntryV3 {
  return {
    status: "unset",
    strokes: null,
    putts: null,
    fairwayHit: null,
    greenInRegulation: null,
    source: "recorded",
    revision: 0,
  };
}
